/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file MailHomeScreen.js
 * @description
 * The primary mailbox screen, available in every app mode. A compact premium
 * header (active mailbox + a "Write email" CTA, a bold folder title with a
 * live "unread · total" subtitle, queued/failed outbox alert chips only when
 * they carry signal, and a pill search field with a compact refresh button)
 * scrolls WITH the message list as its `ListHeaderComponent`, so mail starts
 * high on screen; below it a folder selector, a quick-filter chip row, a
 * paginated message list grouped by day (Today / Yesterday / weekday / date)
 * with pull-to-refresh + infinite scroll, swipe actions
 * (archive / trash) and a multi-select toolbar (select-all-loaded, read/unread,
 * star, archive, move-to-folder via {@link MailFolderPickerSheet}, spam, trash,
 * and — inside Trash only — a confirmed permanent delete). While a mailbox is configured
 * the app's main bottom tab bar hides in favour of the dynamic Mail module bar
 * (Inbox/Sent/Drafts/Archive + an exit pill back to the main navigation — see
 * {@link module:helpers/useModuleNav}). When no mailbox is configured it
 * presents a setup CTA that opens the account wizard. The compose FAB sits
 * above the floating module bar and only fades in once the header compose CTA
 * has scrolled out of view (never while the selection toolbar is active).
 *
 * ### Perceived speed (2026 pass)
 *  - Every successful page fetch feeds {@link module:helpers/mailCache} via
 *    `primeMailMessages`, and a row press fires `prefetchMailMessage` /
 *    `prefetchMailThread` **before** `navigate()`, so `MailThreadScreen` almost
 *    always paints from cache instead of from a cold request.
 *  - The top rows of the first page are prefetched on folder entry (deduped by
 *    the cache), which makes the very first tap instant too.
 *  - The shimmer skeleton is only used for the *first* paint. Refreshes and
 *    folder switches keep the previous page on screen behind a slim "updating"
 *    indicator, so the list never blinks to blank.
 *  - Rows render through a memoized {@link MailRow} wrapper and the FlatList is
 *    tuned (windowSize / batching / clipping) for smooth scrolling.
 *
 * Navigation params: { folder?: string }
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
    ActivityIndicator,
    Animated,
    FlatList,
    Platform,
    RefreshControl,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import {useFocusEffect, useNavigation, useRoute} from '@react-navigation/native';
import {LinearGradient} from 'expo-linear-gradient';
import {useTranslation} from 'react-i18next';
import Toast from 'react-native-toast-message';
import Icon from '../../components/management/SafeIcon';
import ManagementScreen from '../../components/management/ManagementScreen';
import {GradientHero, EmptyState, PrimaryButton, NoteBox} from '../../components/management/ManagementUI';
import {
    MailListItem, FolderPill, MailListSkeleton, QuickFilterBar, MailSectionHeader, MailEmpty,
    MailAccountChip, MailWriteButton, MailFolderPickerSheet,
} from '../../components/mail/MailUiKit';
import {useManagementTheme} from '../../helpers/managementTheme';
import {useMail} from '../../helpers/MailContext';
import {useBottomInset, useTopInset} from '../../helpers/safeArea';
import {pollMailNotificationsNow} from '../../helpers/mailNotifications';
import {prefetchMailMessage, prefetchMailThread, primeMailMessages, toPlainMailPreview} from '../../helpers/mailCache';
import HappyAlert from '../../components/HappyAlert';
import mailApi, {deleteMailDraft, getMailOrganizeSummary, listMailDrafts, listMailMessages, listSnoozes} from '../../services/mailApi';
import {useMailCollab} from '../../helpers/mailCollabStore';
import {assignWithTakeover, toastCollabError} from '../../helpers/mailCollabActions';
import {
    applyViewFilter,
    buildAssignedQuery,
    findAccountIdForAddress,
    findSnooze,
    getConversationKey,
    hideSnoozed,
    resolveMailAddress,
    toViewFilter,
} from '../../helpers/mailCollabUtils';
import {CollabRowMeta} from '../../components/mail/MailCollabParts';
import MailOrganizeFilters from '../../components/mail/MailOrganizeFilters';
import MailOrganizeSheet from '../../components/mail/MailOrganizeSheet';
import MailFolderManagerSheet from '../../components/mail/MailFolderManagerSheet';
import {buildLabelIndex, normalizeOrganizeSummary, organizeFilterParams} from '../../helpers/mailOrganize';
import {MailCollabHomeChips, SnoozedRow} from './collab/MailCollabHomeChips';

const PAGE_SIZE = 25;

/** How many of the newest rows get their full body warmed on folder entry. */
const PREFETCH_TOP_ROWS = 4;

/** Floating module bar geometry (see {@link module:components/module/ModuleTabBar}). */
const MODULE_BAR_HEIGHT = 62;
const MODULE_BAR_OFFSET = 10;

/** Mail module-tab keys ↔ mailbox folder keys (drives the dynamic module bottom bar). */
const FOLDER_TO_MODULE_TAB = {INBOX: 'inbox', Sent: 'sent', Drafts: 'drafts', Archive: 'archive'};
const MODULE_TAB_TO_FOLDER = {inbox: 'INBOX', sent: 'Sent', drafts: 'Drafts', archive: 'Archive'};

/** Folder keys the backend always exposes (used to split standard vs custom). */
const STANDARD_FOLDER_KEYS = ['INBOX', 'Sent', 'Drafts', 'Archive', 'Spam', 'Trash'];

/**
 * Stable message id across the `id` / `_id` shapes the backend may emit.
 * @param {Object} message Message document.
 * @returns {string} The id, or an empty string when it cannot be resolved.
 */
const msgId = (message) => String(message?.id || message?._id || '');

/**
 * Resolve the row's primary correspondent so the reader can paint its header
 * before any request resolves (see the `fromName` / `fromEmail` route params).
 * @param {Object} message Message document.
 * @returns {{name: string, email: string}} Display name + address (may be empty).
 */
const primaryFrom = (message) => {
    const list = Array.isArray(message?.from) ? message.from : [];
    const first = list[0] || {};

    return {name: first.name || message?.fromName || '', email: first.email || message?.fromEmail || ''};
};

/**
 * Shape a draft document into the row the mail list renders.
 *
 * A draft has no sender worth showing (it is your own address) and no stored snippet, so the
 * row would come up blank: mail clients label a draft by WHO IT IS ADDRESSED TO, with a
 * preview derived from the body it already carries.
 *
 * @param {Object} [draft] Draft document from `/mail/drafts`.
 * @returns {Object} Row-shaped draft.
 */
const toDraftRow = (draft = {}) => {
    const recipients = Array.isArray(draft.to) ? draft.to : [];

    return {
        ...draft,
        from: recipients[0] || draft.from || {name: '', email: ''},
        previewText: draft.previewText
            || toPlainMailPreview(draft.textBody || draft.bodyText || draft.htmlBody || draft.bodyHtml || ''),
        // Drafts are never "unread" — the bold unread styling would be noise here.
        isRead: true,
    };
};


/**
 * Folder label + icon for the standard mailbox folders.
 * @param {string} key Folder key.
 * @param {Function} t i18n translator.
 * @returns {{label: string, icon: string}} Presentation metadata.
 */
const folderMeta = (key, t) => {
    const map = {
        INBOX: {label: t('mail_folder_inbox'), icon: 'inbox'},
        Sent: {label: t('mail_folder_sent'), icon: 'send'},
        Drafts: {label: t('mail_folder_drafts'), icon: 'file-document-edit-outline'},
        Archive: {label: t('mail_folder_archive'), icon: 'archive-outline'},
        Spam: {label: t('mail_folder_spam'), icon: 'alert-octagon-outline'},
        Trash: {label: t('mail_folder_trash'), icon: 'trash-can-outline'},
    };

    return map[key] || {label: key, icon: 'folder-outline'};
};

/**
 * Bucket a message into a per-day date group. `key` is a stable day key used
 * for the synthetic section rows' React keys; `label` is what the header shows
 * (Today / Yesterday / a weekday name inside the last week / a short date
 * beyond that, with the year added once it differs from the current one).
 * @param {string|number|Date} value Timestamp to bucket.
 * @param {Function} t i18n translator.
 * @param {string} locale BCP-47 language tag used for weekday/date labels.
 * @returns {{key: string, label: string}} Stable day key + localized label.
 */
const dateBucket = (value, t, locale) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return {key: 'unknown', label: t('mail_group_earlier')};
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const key = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    if (startOfDay >= startOfToday) return {key, label: t('mail_group_today')};
    if (startOfDay >= startOfToday - 86400000) return {key, label: t('mail_group_yesterday')};
    try {
        if (startOfDay >= startOfToday - 6 * 86400000) return {key, label: d.toLocaleDateString(locale, {weekday: 'long'})};

        return {key, label: d.toLocaleDateString(locale, d.getFullYear() === now.getFullYear() ? {month: 'short', day: 'numeric'} : {year: 'numeric', month: 'short', day: 'numeric'})};
    } catch (_e) {
        return {key, label: d.toLocaleDateString()};
    }
};

/**
 * Memoized list row. Keeping the per-row closures *inside* a `React.memo`
 * wrapper (instead of rebuilding them in `renderItem`) means a row only
 * re-renders when its own message / selection state actually changes — which is
 * what keeps scrolling smooth while the selection array or a background refresh
 * mutates screen state.
 *
 * @param {object} props
 * @param {Object} props.message        Message document.
 * @param {boolean} props.selected      Row is part of the current selection.
 * @param {boolean} props.selectionMode Multi-select toolbar is active.
 * @param {boolean} props.canArchive    Enable the archive swipe action.
 * @param {boolean} props.canTrash      Enable the trash swipe action.
 * @param {(message: Object) => void} props.onOpen  Open the message / draft.
 * @param {(id: string) => void} props.onSelect     Toggle selection for the row.
 * @param {(message: Object) => void} props.onStar  Toggle the star flag.
 * @param {(id: string) => void} props.onArchive    Archive the message.
 * @param {(id: string) => void} props.onTrash      Move the message to trash.
 * @param {Function} props.t  Translator from `useTranslation`; referentially
 *        stable per language, so passing it through does not defeat memoization.
 * @param {boolean} [props.shared]       Row belongs to a shared mailbox (collaboration meta).
 * @param {Object|null} [props.collabState] Thread state of the row (shared mailboxes).
 * @param {Object[]} [props.mailboxTags] Team tags of the row's shared mailbox.
 * @param {Object|null} [props.snooze]   Pending snooze of the row.
 * @param {(message: Object) => void} [props.onClaim] Claim an unassigned thread.
 * @param {Object} [props.labelIndex] Label id -> {name,color} index (resolves row label chips).
 * @returns {JSX.Element}
 */
const MailRow = React.memo(function MailRow({
    message,
    selected,
    selectionMode,
    canArchive,
    canTrash,
    onOpen,
    onSelect,
    onStar,
    onArchive,
    onTrash,
    t,
    shared,
    collabState,
    mailboxTags,
    snooze,
    onClaim,
    labelIndex,
}) {
    const id = msgId(message);
    // Shared-mailbox collaboration meta (replied-by, assignee / Claim, team tags, Done, snooze clock).
    const collabMeta = (shared || snooze) ? (
        <CollabRowMeta
            state={collabState}
            mailboxTags={mailboxTags}
            snooze={snooze}
            shared={Boolean(shared)}
            onClaim={onClaim ? () => onClaim(message) : undefined}
        />
    ) : undefined;

    return (
        <MailListItem
            t={t}
            message={message}
            selected={selected}
            selectionMode={selectionMode}
            onPress={() => (selectionMode ? onSelect(id) : onOpen(message))}
            onLongPress={() => onSelect(id)}
            onToggleStar={() => onStar(message)}
            onArchive={canArchive ? () => onArchive(id) : undefined}
            onTrash={canTrash ? () => onTrash(id) : undefined}
            collabMeta={collabMeta}
            labelIndex={labelIndex}
        />
    );
});

/**
 * Primary mailbox screen.
 * @returns {JSX.Element}
 */
const MailHomeScreen = () => {
    const {t, i18n} = useTranslation();
    const m = useManagementTheme();
    const navigation = useNavigation();
    const route = useRoute();
    const topInset = useTopInset();
    const bottomInset = useBottomInset();
    const mail = useMail();
    const collab = useMailCollab();
    const {fetchAssignedKeys, requestStates} = collab;
    // Collaboration view: null | {address, view} | {address, tag} | {personalTag} | {snoozed: true}.
    const [collabView, setCollabView] = useState(null);
    const [viewFilter, setViewFilter] = useState(null);
    const viewKeyRef = useRef('');

    const [activeFolder, setActiveFolder] = useState(route.params?.folder || 'INBOX');
    const [messages, setMessages] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [listBusy, setListBusy] = useState(false);
    const [error, setError] = useState(null);
    const [selection, setSelection] = useState([]);
    const [quickFilter, setQuickFilter] = useState('all');
    const [movePickerOpen, setMovePickerOpen] = useState(false);
    // Organize (colour / AI category / label filter, sheet and per-dimension counts) - every account.
    const [organizeFilter, setOrganizeFilter] = useState(null);
    const [folderManagerOpen, setFolderManagerOpen] = useState(false);
    const [emptying, setEmptying] = useState(false);
    const [organizeOpen, setOrganizeOpen] = useState(false);
    const [organizeSummary, setOrganizeSummary] = useState(() => normalizeOrganizeSummary(null));
    // Compose FAB reveal: hidden while the header's compose CTA is on screen,
    // fading/scaling in once the list scrolls it out of view.
    const [fabVisible, setFabVisible] = useState(false);
    const fabAnim = useRef(new Animated.Value(0)).current;
    const fabShownRef = useRef(false);
    const fetchSeq = useRef(0);
    // Mirrors `messages` so the folder-change effect can decide between
    // "first paint → skeleton" and "we already show something → keep it"
    // without re-running whenever the list content changes.
    const messagesRef = useRef([]);
    // Folder whose page is currently on screen — lets a failed fetch keep the
    // old rows for a same-folder refresh but clear them on a real folder switch.
    const renderedFolderRef = useRef(activeFolder);

    const selectionMode = selection.length > 0;
    const selectedSet = useMemo(() => new Set(selection), [selection]);
    const selectedMessages = useMemo(
        () => messages.filter((x) => selectedSet.has(msgId(x))),
        [messages, selectedSet],
    );
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    // Dynamic module bottom bar: while Mail is configured, the main app tab bar
    // hides and the module bar (Inbox/Sent/Drafts/Archive + exit pill) takes its
    // place. Swapping a module tab just re-points `activeFolder` — same fetch path.
    useLayoutEffect(() => {
        navigation.setOptions({tabBarStyle: mail.configured ? {display: 'none'} : undefined});
    }, [navigation, mail.configured]);
    const activeModuleTab = FOLDER_TO_MODULE_TAB[activeFolder];
    const onModuleView = useCallback((view) => {
        setActiveFolder(MODULE_TAB_TO_FOLDER[view] || 'INBOX');
    }, []);
    const moduleBadges = useMemo(() => ({
        inbox: mail.unreadCount > 0 ? mail.unreadCount : undefined,
    }), [mail.unreadCount]);
    const exitToMainNav = useCallback(() => navigation.navigate('ManagementDashboard'), [navigation]);

    // Active mailbox (design's account chip) — falls back to the first account.
    const activeAccount = useMemo(() => {
        const list = Array.isArray(mail.accounts) ? mail.accounts : [];

        return list.find((a) => a.id === mail.activeAccountId) || list[0] || null;
    }, [mail.accounts, mail.activeAccountId]);

    // Server scope of the active collaboration view: a shared mailbox's own account, or a personal
    // tag (= mailbox label). Smart views / team tags additionally filter by conversation key below.
    const collabScope = useMemo(() => {
        if (!collabView) return {};
        if (collabView.personalTag) return {label: collabView.personalTag};
        if (collabView.address) {
            const accountId = findAccountIdForAddress(collabView.address, mail.accounts);

            return accountId ? {mailboxAccountId: accountId} : {};
        }

        return {};
    }, [collabView, mail.accounts]);
    const snoozedView = Boolean(collabView?.snoozed);

    // Client-side quick filter over the already-fetched page.
    const filteredMessages = useMemo(() => {
        const base = Array.isArray(messages) ? messages : [];
        let list = base;
        if (quickFilter === 'unread') list = base.filter((x) => !x.isRead && x.direction !== 'outgoing');
        else if (quickFilter === 'starred') list = base.filter((x) => x.isStarred);
        else if (quickFilter === 'attachments') list = base.filter((x) => Array.isArray(x.attachments) && x.attachments.length > 0);

        // Collaboration smart view / team tag: filter by the conversation keys the backend returned.
        // While the keys are still loading (`viewFilter` null) nothing is shown instead of everything.
        if (collabView?.address) {
            list = viewFilter ? applyViewFilter(list, viewFilter) : [];
        }

        // Snoozed conversations are hidden until they come due (the Snoozed view lists them).
        return hideSnoozed(list, collab.snoozeIndex);
    }, [messages, quickFilter, collabView, viewFilter, collab.snoozeIndex]);

    // "Select all (loaded)" toggles every currently client-filtered row.
    const allLoadedIds = useMemo(
        () => filteredMessages.map((msg) => msgId(msg)).filter(Boolean),
        [filteredMessages],
    );
    const allLoadedSelected = allLoadedIds.length > 0 && allLoadedIds.every((id) => selectedSet.has(id));
    const toggleSelectAllLoaded = useCallback(() => {
        setSelection(allLoadedSelected ? [] : allLoadedIds);
    }, [allLoadedSelected, allLoadedIds]);

    // Flatten into [{__section}, ...msgs] so the FlatList shows per-day
    // date-group headers (Today / Yesterday / weekday / date). Section keys are
    // day-stable, so `keyExtractor` stays stable across refreshes and paging.
    // Row heights are intentionally variable (label chips are optional), so no
    // `getItemLayout` is supplied — a wrong fixed height there breaks scroll
    // offsets far worse than the measurement it would save.
    const listData = useMemo(() => {
        // "Snoozed" smart view: the pending snoozes themselves (subject / sender / wake-up time).
        if (snoozedView) {
            return collab.snoozes.map((snooze) => ({__snooze: true, key: `snz-${snooze._id || snooze.id}`, snooze}));
        }
        const out = [];
        const locale = i18n?.language || 'hu';
        let lastKey = null;
        filteredMessages.forEach((msg) => {
            const bucket = dateBucket(msg.receivedAt || msg.sentAt || msg.createdAt, t, locale);
            if (bucket.key !== lastKey) {
                out.push({__section: true, key: `sec-${bucket.key}`, label: bucket.label});
                lastKey = bucket.key;
            }
            out.push(msg);
        });

        return out;
    }, [filteredMessages, t, i18n, snoozedView, collab.snoozes]);

    // Smart views / team tags: fetch the include/exclude conversation-key filter and re-fetch it
    // whenever a thread state changes (realtime `mail:thread_state` bumps `stateVersion`).
    useEffect(() => {
        if (!collabView?.address) {
            viewKeyRef.current = '';
            setViewFilter(null);

            return undefined;
        }
        const viewKey = JSON.stringify(collabView);
        let cancelled = false;

        if (viewKey !== viewKeyRef.current) {
            viewKeyRef.current = viewKey;
            setViewFilter(null);
        }
        fetchAssignedKeys(buildAssignedQuery(collabView))
            .then((result) => {
                if (!cancelled) setViewFilter(toViewFilter(collabView, result));
            })
            .catch((e) => {
                if (!cancelled) {
                    setViewFilter({mode: 'exclude', keys: new Set(), truncated: false});
                    toastCollabError(t, e);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [collabView, collab.stateVersion, mail.mutationTick, fetchAssignedKeys, t]);

    // Thread states of the visible rows that belong to a shared mailbox (debounced + deduped by the store).
    useEffect(() => {
        if (!collab.sharedMailboxes.length) return;
        const byAddress = {};
        filteredMessages.forEach((msg) => {
            const address = resolveMailAddress(msg, mail.accounts);
            const key = getConversationKey(msg);
            if (address && key && collab.getSharedMailbox(address)) {
                (byAddress[address] = byAddress[address] || []).push(key);
            }
        });
        Object.entries(byAddress).forEach(([address, keys]) => requestStates(address, keys));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredMessages, mail.accounts, collab.sharedMailboxes, requestStates]);

    /** Select a collaboration view; entering a smart view jumps to the Inbox. */
    const onCollabChange = useCallback((next) => {
        setCollabView(next);
        setSelection([]);
        if (next && !next.snoozed) setActiveFolder('INBOX');
    }, []);

    /** Claim an unassigned shared-mailbox thread for the signed-in user. */
    const meId = collab.meId;
    const onClaim = useCallback((message) => {
        const address = resolveMailAddress(message, mail.accounts);
        const conversationKey = getConversationKey(message);
        if (!address || !conversationKey || !meId) return;
        assignWithTakeover({
            collab,
            address,
            conversationKey,
            assigneeId: meId,
            subject: message.subject,
            state: collab.getState(address, conversationKey),
            t,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mail.accounts, meId, t]);

    // Push tap (`mail.assigned` …) lands here with `collabOpen = {conversationKey, mailboxAddress}`: the
    // push carries no message id, so resolve it from the loaded rows, else from a fired snooze.
    const collabOpen = route.params?.collabOpen;
    const [pendingOpen, setPendingOpen] = useState(null);
    useEffect(() => {
        if (!collabOpen?.conversationKey) return;
        const target = {
            conversationKey: String(collabOpen.conversationKey).toLowerCase(),
            address: String(collabOpen.mailboxAddress || '').toLowerCase(),
        };
        setPendingOpen(target);
        navigation.setParams({collabOpen: undefined});
        if (target.address && collab.getSharedMailbox(target.address)) {
            setCollabView({address: target.address, view: 'open'});
            setActiveFolder('INBOX');
        }
        // Resolve through a FIRED snooze (mail.snooze_due) — it carries the message / thread ids.
        listSnoozes({status: 'fired'})
            .then((res) => {
                const hit = (Array.isArray(res?.snoozes) ? res.snoozes : [])
                    .find((row) => String(row.conversationKey || '').toLowerCase() === target.conversationKey);
                if (hit && (hit.messageId || hit.threadKey)) {
                    setPendingOpen(null);
                    navigation.navigate('MailThreadScreen', {
                        messageId: hit.messageId,
                        threadKey: hit.threadKey,
                        subject: hit.subject,
                        fromName: hit.fromName,
                    });
                }
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collabOpen]);

    const quickFilterOptions = useMemo(() => ([
        {key: 'all', label: t('mail_filter_all'), icon: 'email-multiple-outline'},
        {key: 'unread', label: t('mail_filter_unread'), icon: 'email-outline', count: mail.unreadCount},
        {key: 'starred', label: t('mail_filter_starred'), icon: 'star-outline'},
        {key: 'attachments', label: t('mail_filter_attachments'), icon: 'paperclip'},
    ]), [t, mail.unreadCount]);

    /** Build a normalized folder list: standard folders + custom + counts. */
    const folderChips = useMemo(() => {
        const counts = {};
        (Array.isArray(mail.folders) ? mail.folders : []).forEach((f) => {
            const key = f.key || f.name || f.id;
            if (key) counts[key] = f.unreadCount ?? f.unread ?? 0;
        });
        // Per-folder unread from the organize summary (covers every account, custom folders included).
        organizeSummary.folders.forEach((f) => {
            if (f.key && counts[f.key] === undefined) counts[f.key] = f.unread || 0;
        });
        const standard = STANDARD_FOLDER_KEYS.map((key) => ({
            key,
            ...folderMeta(key, t),
            count: key === 'INBOX' ? mail.unreadCount : (counts[key] || 0),
        }));
        const custom = (Array.isArray(mail.folders) ? mail.folders : [])
            .filter((f) => !STANDARD_FOLDER_KEYS.includes(f.key || f.name))
            .map((f) => {
                const key = f.key || f.id || f.name;
                // `items[].label` is only the folder id — the renamed display name lives on the directory.
                const dir = (Array.isArray(mail.directories) ? mail.directories : []).find((d) => d.id === key);

                return {key, label: dir?.name || f.name || f.label || key, color: f.color || dir?.color, count: f.unreadCount || 0};
            });

        return [...standard, ...custom];
    }, [mail.folders, mail.directories, mail.unreadCount, organizeSummary.folders, t]);

    /**
     * Fetch one page of the mailbox. Always primes the shared mail cache so the
     * reader can paint from memory; never blanks the list on a same-folder error.
     *
     * @param {string} folder            Folder key to list.
     * @param {number} pageNum           1-based page index.
     * @param {object} [opts]            Fetch options.
     * @param {boolean} [opts.append]    Append (infinite scroll) instead of replace.
     * @param {boolean} [opts.prefetch]  Warm the top rows' bodies after the fetch.
     * @returns {Promise<void>} Resolves once the page has been applied (or failed).
     */
    const fetchPage = useCallback(async (folder, pageNum, {append = false, prefetch = false} = {}) => {
        const seq = ++fetchSeq.current;
        try {
            const params = {folder, page: pageNum, limit: PAGE_SIZE};
            if (folder === 'Sent') {
                params.direction = 'outgoing';
            }
            // Collaboration scope (shared mailbox account / personal tag label) — additive params.
            Object.assign(params, collabScope);
            // Organize filter (colour / category / label) - additive list params.
            Object.assign(params, organizeFilterParams(organizeFilter));
            // Quick filters (Unread/Starred/Attachments) are server-backed so they
            // cover the whole mailbox, not just the page(s) already fetched — the
            // client-side `filteredMessages` memo below still applies on top as an
            // instant optimistic overlay over the previous page while this fetch
            // (triggered by the folder-change effect whenever `fetchPage`'s
            // identity changes, i.e. also on a quick-filter change) is in flight.
            if (quickFilter === 'unread') {
                params.unread = true;
            } else if (quickFilter === 'starred') {
                params.starred = true;
            } else if (quickFilter === 'attachments') {
                params.hasAttachments = true;
            }
            // Drafts live in their own collection — `/mail/messages?folder=Drafts` can never
            // return them, which is why the Drafts tab came up empty. They also carry none of
            // the message-only flags the quick filters use, so `params` is not sent along.
            const res = folder === 'Drafts'
                ? await listMailDrafts({page: pageNum, limit: PAGE_SIZE})
                : await listMailMessages(params);
            if (seq !== fetchSeq.current) return; // stale
            // The backend returns the `mongoose-paginate` shape `{docs, total}`
            // (see MailService.listMailboxMessages). Read `docs`/`total` first;
            // keep the legacy keys as fallbacks for safety.
            const rawItems = Array.isArray(res?.docs) ? res.docs
                : Array.isArray(res?.items) ? res.items
                    : Array.isArray(res?.messages) ? res.messages
                        : Array.isArray(res) ? res
                            : [];
            // A draft row carries no sender/snippet of its own — see {@link toDraftRow}.
            const items = folder === 'Drafts' ? rawItems.map(toDraftRow) : rawItems;
            const totalCount = res?.total ?? res?.totalDocs ?? res?.totalCount ?? items.length;
            // Feed the reader cache with every row we just received.
            primeMailMessages(items);
            setMessages((prev) => (append ? [...prev, ...items] : items));
            setTotal(Number(totalCount) || 0);
            setHasMore((pageNum * PAGE_SIZE) < totalCount && items.length > 0);
            setPage(pageNum);
            setError(null);
            renderedFolderRef.current = folder;
            // Warm the newest rows so the very first tap opens instantly. The
            // cache dedupes ids it already holds / has in flight, so this is cheap.
            if (prefetch && folder !== 'Drafts') {
                items.slice(0, PREFETCH_TOP_ROWS).forEach((item) => {
                    const id = msgId(item);
                    if (id) prefetchMailMessage(id);
                });
            }
        } catch (e) {
            if (seq !== fetchSeq.current) return;
            if (!append) {
                setError(e);
                // A failed *folder switch* must not keep showing the previous
                // folder's mail; a failed refresh of the same folder keeps it.
                if (renderedFolderRef.current !== folder) {
                    setMessages([]);
                    setTotal(0);
                    setHasMore(false);
                    renderedFolderRef.current = folder;
                }
            }
            if (e?.code !== 'AUTH_REQUIRED') {
                Toast.show({type: 'error', text1: t('mail_error'), text2: e?.message});
            }
        } finally {
            if (seq === fetchSeq.current) {
                setLoading(false);
                setLoadingMore(false);
                setRefreshing(false);
                setListBusy(false);
            }
        }
    }, [t, quickFilter, collabScope, organizeFilter]);

    // Organize summary (folders / colours / categories / labels with counts) - refreshed on every mutation.
    const labelIndex = useMemo(() => buildLabelIndex(mail.labels), [mail.labels]);
    useEffect(() => {
        if (!mail.configured) return undefined;
        let cancelled = false;
        getMailOrganizeSummary(mail.activeAccountId || undefined)
            .then((res) => {
                if (!cancelled) setOrganizeSummary(normalizeOrganizeSummary(res));
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [mail.configured, mail.activeAccountId, mail.mutationTick]);

    // Refetch when folder changes or when a mutation invalidates the list.
    useEffect(() => {
        if (!mail.configured) {
            setLoading(false);

            return;
        }
        setSelection([]);
        // Only the very first paint (nothing on screen yet) shows the skeleton —
        // refreshes and folder switches keep the previous page visible.
        if (!messagesRef.current.length) {
            setLoading(true);
        }
        setListBusy(true);
        fetchPage(activeFolder, 1, {prefetch: true});
    }, [activeFolder, mail.configured, mail.mutationTick, fetchPage]);

    // Refresh status/folders whenever the screen gains focus, and surface a
    // local banner for any mail that arrived while the app was idle.
    const refreshStatus = mail.refreshStatus;
    useFocusEffect(useCallback(() => {
        refreshStatus();
        pollMailNotificationsNow();
    }, [refreshStatus]));

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        await Promise.all([mail.syncNow().catch(() => {}), fetchPage(activeFolder, 1)]);
    }, [mail, activeFolder, fetchPage]);

    const onEndReached = useCallback(() => {
        if (hasMore && !loadingMore && !loading && !snoozedView) {
            setLoadingMore(true);
            fetchPage(activeFolder, page + 1, {append: true});
        }
    }, [hasMore, loadingMore, loading, activeFolder, page, fetchPage, snoozedView]);

    /**
     * List scroll listener — reveals the compose FAB with a subtle fade/scale
     * only after the scrolling header's compose CTA has left the viewport.
     * @param {object} e Native scroll event.
     * @returns {void}
     */
    const onListScroll = useCallback((e) => {
        const y = e?.nativeEvent?.contentOffset?.y || 0;
        const next = y > topInset + 64;
        if (next === fabShownRef.current) return;
        fabShownRef.current = next;
        setFabVisible(next);
        Animated.timing(fabAnim, {toValue: next ? 1 : 0, duration: 160, useNativeDriver: true}).start();
    }, [topInset, fabAnim]);

    const retryFetch = useCallback(() => {
        setError(null);
        setLoading(true);
        fetchPage(activeFolder, 1, {prefetch: true});
    }, [activeFolder, fetchPage]);

    const openCompose = useCallback(() => navigation.navigate('MailComposeScreen', {mode: 'new'}), [navigation]);

    const openMessage = useCallback((message) => {
        const id = msgId(message);
        if (activeFolder === 'Drafts') {
            navigation.navigate('MailComposeScreen', {mode: 'draft', draftId: id});

            return;
        }
        // Warm the reader BEFORE navigating: the request starts while the screen
        // transition animates, so the body is usually cached by the time the
        // reader mounts instead of being requested from a cold start.
        if (id) prefetchMailMessage(id);
        if (message?.threadKey) prefetchMailThread(message.threadKey);
        if (!message.isRead && message.direction !== 'outgoing') {
            mail.markRead([id], true).catch(() => {});
        }
        const from = primaryFrom(message);
        navigation.navigate('MailThreadScreen', {
            messageId: id,
            threadKey: message.threadKey,
            folder: activeFolder,
            // Optional pre-paint params (shared reader contract) so the header
            // is filled in even before anything resolves.
            subject: message.subject,
            fromName: from.name,
            fromEmail: from.email,
            preview: message.previewText,
        });
    }, [activeFolder, navigation, mail]);

    // Finish a push-tap deep link once the target conversation shows up in the loaded rows.
    useEffect(() => {
        if (!pendingOpen) return;
        const hit = messages.find((msg) => getConversationKey(msg) === pendingOpen.conversationKey);
        if (hit) {
            setPendingOpen(null);
            openMessage(hit);
        }
    }, [pendingOpen, messages, openMessage]);

    /** Open the thread of a snoozed conversation (the reader offers Cancel snooze). */
    const openSnoozed = useCallback((snooze) => {
        navigation.navigate('MailThreadScreen', {
            messageId: snooze.messageId,
            threadKey: snooze.threadKey,
            subject: snooze.subject,
            fromName: snooze.fromName,
        });
    }, [navigation]);

    /** Cancel a pending snooze from the Snoozed view. */
    const cancelSnoozed = useCallback((snooze) => {
        HappyAlert.show({
            type: 'question',
            title: t('mail_collab_snooze_cancel_title'),
            message: snooze.subject || t('mail_no_subject'),
            buttons: [
                {text: t('cancel'), style: 'cancel'},
                {
                    text: t('mail_collab_snooze_cancel'),
                    onPress: () => collab.cancelSnooze(snooze._id || snooze.id).catch((e) => toastCollabError(t, e)),
                },
            ],
        });
    }, [collab, t]);

    const toggleSelect = useCallback((id) => {
        setSelection((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }, []);

    const runBulk = useCallback(async (fn) => {
        try {
            await fn(selection);
            setSelection([]);
        } catch (e) {
            Toast.show({type: 'error', text1: t('mail_error'), text2: e?.message});
        }
    }, [selection, t]);

    /**
     * Move the whole selection into `folderKey` from the folder picker sheet,
     * then refresh the per-folder counts (the context wrapper already bumps the
     * mutation tick, which refetches page 1 and clears the selection).
     * @param {string} folderKey Target folder key (standard or custom).
     * @returns {void}
     */
    const onMoveToFolder = useCallback((folderKey) => {
        setMovePickerOpen(false);
        runBulk(async (ids) => {
            await mail.moveMessages(ids, folderKey);
            await mail.refreshFolders();
        });
    }, [runBulk, mail]);

    /**
     * Permanently purge the selection — only reachable from the Trash folder —
     * behind a destructive {@link HappyAlert} confirm (same premium dialog the
     * composer uses for its discard confirm). After the purge the status +
     * folder counts are refreshed and the mutation tick is bumped so the list
     * refetches page 1, mirroring what the context-wrapped actions do.
     * @returns {void}
     */
    const confirmDeleteForever = useCallback(() => {
        HappyAlert.show({
            type: 'question',
            title: t('mail_delete_forever_title'),
            message: t('mail_delete_forever_message'),
            buttons: [
                {text: t('cancel'), style: 'cancel'},
                {
                    text: t('mail_delete_forever'),
                    style: 'destructive',
                    onPress: () => runBulk(async (ids) => {
                        await mailApi.deleteMailMessages({ids, permanent: true});
                        await Promise.all([mail.refreshStatus(), mail.refreshFolders()]);
                        mail.invalidate();
                    }),
                },
            ],
        });
    }, [t, runBulk, mail]);

    /**
     * Empty the Trash or Spam folder behind a destructive {@link HappyAlert} confirm, then reload.
     * Trash and Spam each use their dedicated server-side purge endpoint
     * (`POST /mail/messages/trash/empty` / `POST /mail/messages/spam/empty`).
     * @returns {void}
     */
    const confirmEmptyFolder = useCallback(() => {
        const folder = activeFolder;
        if (folder !== 'Trash' && folder !== 'Spam') {
            return;
        }
        HappyAlert.show({
            type: 'question',
            title: folder === 'Trash' ? t('mail_empty_trash_title') : t('mail_empty_spam_title'),
            message: t('mail_empty_folder_message'),
            buttons: [
                {text: t('cancel'), style: 'cancel'},
                {
                    text: folder === 'Trash' ? t('mail_empty_trash') : t('mail_empty_spam'),
                    style: 'destructive',
                    onPress: async () => {
                        setEmptying(true);
                        try {
                            if (folder === 'Trash') {
                                await mailApi.emptyMailTrash();
                            } else {
                                await mailApi.emptyMailSpam();
                            }
                            Toast.show({type: 'success', text1: t('mail_empty_folder_done')});
                        } catch (e) {
                            Toast.show({type: 'error', text1: t('mail_error'), text2: e?.message});
                        } finally {
                            setEmptying(false);
                            await Promise.all([mail.refreshStatus(), mail.refreshFolders()]);
                            mail.invalidate();
                            fetchPage(folder, 1);
                        }
                    },
                },
            ],
        });
    }, [activeFolder, t, mail, fetchPage]);

    // Folders offered by the move-to-folder sheet: every standard + custom
    // folder except the one already on screen (and Drafts, which only holds
    // unsent local compositions).
    const moveTargets = useMemo(
        () => folderChips.filter((f) => f.key !== activeFolder && f.key !== 'Drafts'),
        [folderChips, activeFolder],
    );

    /* Stable per-row handlers — required for `MailRow`'s memoization to bite. */
    const starMessages = mail.starMessages;
    const archiveMessages = mail.archiveMessages;
    const trashMessages = mail.trashMessages;
    const onRowStar = useCallback((message) => {
        starMessages([msgId(message)], !message?.isStarred);
    }, [starMessages]);
    const onRowArchive = useCallback((id) => archiveMessages([id]), [archiveMessages]);
    /**
     * Trash one row. A draft is NOT a message — the trash endpoint does not know draft ids —
     * so the Drafts tab deletes it through `/mail/drafts/:id` and refetches page 1 itself.
     * @param {string} id Row id (message or draft).
     * @returns {Promise<void>}
     */
    const onRowTrash = useCallback(async (id) => {
        if (activeFolder !== 'Drafts') {
            trashMessages([id]);

            return;
        }
        try {
            await deleteMailDraft(id);
            fetchPage('Drafts', 1);
        } catch (e) {
            Toast.show({type: 'error', text1: t('mail_error'), text2: e?.message});
        }
    }, [activeFolder, trashMessages, fetchPage, t]);

    const canArchive = activeFolder !== 'Archive';
    const canTrash = activeFolder !== 'Trash';

    /**
     * Folder-aware premium empty state: each folder gets its own icon, a
     * one-line explanation and a meaningful action (sync for incoming folders,
     * compose for Sent/Drafts). Quick-filter empties are handled separately at
     * the `ListEmptyComponent` call site.
     */
    const folderEmpty = useMemo(() => {
        const refresh = {actionLabel: t('mail_sync'), actionIcon: 'refresh', onAction: onRefresh};
        const compose = {actionLabel: t('mail_write_button'), actionIcon: 'pencil', onAction: openCompose};
        const map = {
            INBOX: {icon: 'email-check-outline', title: t('mail_all_caught_up'), text: t('mail_empty_inbox_text', {defaultValue: 'No new mail — nothing needs your attention.'}), ...refresh},
            Sent: {icon: 'send', text: t('mail_empty_sent_text', {defaultValue: 'Mail you send will show up here.'}), ...compose},
            Drafts: {icon: 'file-document-edit-outline', text: t('mail_empty_drafts_text', {defaultValue: 'Drafts you save will wait for you here.'}), ...compose},
            Archive: {icon: 'archive-outline', text: t('mail_empty_archive_text', {defaultValue: 'Messages you archive will show up here.'}), ...refresh},
            Spam: {icon: 'shield-check-outline', text: t('mail_empty_spam_text', {defaultValue: 'No spam — your mailbox is clean.'}), ...refresh},
            Trash: {icon: 'trash-can-outline', text: t('mail_empty_trash_text', {defaultValue: 'Trash is empty.'}), ...refresh},
        };

        return map[activeFolder] || {icon: 'email-outline', text: t('mail_empty_text'), ...refresh};
    }, [activeFolder, t, onRefresh, openCompose]);

    const renderItem = useCallback(({item}) => {
        if (item.__snooze) {
            return (
                <View style={styles.rowWrap}>
                    <SnoozedRow snooze={item.snooze} onOpen={openSnoozed} onCancel={cancelSnoozed} />
                </View>
            );
        }
        if (item.__section) {
            return (
                <View style={styles.rowWrap}>
                    <MailSectionHeader label={item.label} />
                </View>
            );
        }

        const address = resolveMailAddress(item, mail.accounts);
        const conversationKey = getConversationKey(item);
        const mailbox = address && conversationKey ? collab.getSharedMailbox(address) : null;
        const collabState = mailbox ? collab.getState(address, conversationKey) : null;

        return (
            <View style={styles.rowWrap}>
                <MailRow
                    shared={Boolean(mailbox)}
                    collabState={collabState}
                    mailboxTags={mailbox?.tags}
                    snooze={findSnooze(item, collab.snoozeIndex)}
                    onClaim={mailbox && meId ? onClaim : undefined}
                    labelIndex={labelIndex}
                    message={item}
                    selected={selectedSet.has(msgId(item))}
                    selectionMode={selectionMode}
                    canArchive={canArchive}
                    canTrash={canTrash}
                    onOpen={openMessage}
                    onSelect={toggleSelect}
                    onStar={onRowStar}
                    onArchive={onRowArchive}
                    onTrash={onRowTrash}
                    t={t}
                />
            </View>
        );
    // `collab.stateVersion` / `collab.mailboxes` / `collab.snoozeIndex` re-create the renderer so rows repaint on collaboration changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSet, selectionMode, canArchive, canTrash, openMessage, toggleSelect, onRowStar, onRowArchive, onRowTrash, t, openSnoozed, cancelSnoozed, mail.accounts, collab.stateVersion, collab.mailboxes, collab.snoozeIndex, meId, onClaim, labelIndex]);

    const keyExtractor = useCallback((item, index) => (
        (item?.__section || item?.__snooze) ? String(item.key) : (msgId(item) || `mail-${index}`)
    ), []);

    /* Slim "updating" strip above the rows — the message count lives in the
     * header's subtitle now, so this only appears while a refetch is running. */
    const listHeader = useMemo(() => {
        if (!listBusy || refreshing || !listData.length) return null;

        return (
            <View style={styles.summaryRow}>
                <ActivityIndicator size="small" color={m.colors.primary} />
                <Text style={[styles.summaryText, {color: m.colors.muted}]}>
                    {t('mail_updating', {defaultValue: 'Updating…'})}
                </Text>
            </View>
        );
    }, [listData.length, listBusy, refreshing, m.colors.muted, m.colors.primary, t]);

    /* Infinite-scroll footer / end-of-list marker. */
    const listFooter = useMemo(() => {
        if (loadingMore) {
            return (
                <View style={styles.footerRow}>
                    <ActivityIndicator size="small" color={m.colors.primary} />
                    <Text style={[styles.footerText, {color: m.colors.muted}]}>{t('mail_loading_more')}</Text>
                </View>
            );
        }
        if (!hasMore && filteredMessages.length > 4) {
            return (
                <View style={styles.footerRow}>
                    <Icon name="check-circle-outline" size={15} color={m.colors.muted} />
                    <Text style={[styles.footerText, {color: m.colors.muted}]}>{t('mail_all_caught_up')}</Text>
                </View>
            );
        }

        return <View style={styles.footerSpacer} />;
    }, [loadingMore, hasMore, filteredMessages.length, m.colors.primary, m.colors.muted, t]);

    /* ── Not configured: setup CTA ── */
    if (!mail.configured && !mail.loading) {
        return (
            <ManagementScreen contentContainerStyle={{paddingTop: topInset + 12}}>
                <GradientHero
                    eyebrow={t('mail_eyebrow')}
                    title={t('mail_title')}
                    subtitle={t('mail_setup_subtitle')}
                    icon="email-fast-outline"
                />
                <EmptyState icon="email-plus-outline" title={t('mail_setup_title')} text={t('mail_setup_text')} />
                <View style={{marginTop: 18}}>
                    <PrimaryButton label={t('mail_setup_cta')} icon="email-edit-outline" onPress={() => navigation.navigate('MailAccountWizardScreen')} />
                </View>
                <View style={{marginTop: 14}}>
                    <NoteBox icon="shield-check-outline">{t('mail_setup_note')}</NoteBox>
                </View>
            </ManagementScreen>
        );
    }

    const showSkeleton = !snoozedView && loading && listData.length === 0;
    const showError = !snoozedView && !!error && !loading && filteredMessages.length === 0;
    // Keep the compose FAB clear of the floating module bar AND the home
    // indicator / gesture bar underneath it.
    const fabBottom = MODULE_BAR_OFFSET + MODULE_BAR_HEIGHT + 12 + bottomInset;

    /* Premium header — scrolls WITH the list (ListHeaderComponent) so mail
     * starts high on screen and the compose CTA can hand over to the FAB.
     * `paddingTop` stays constant across selection-mode toggles — varying it
     * jumped every row by ~topInset px on long-press/deselect since the scroll
     * offset is preserved while the header's own height changes underneath it. */
    const headerContent = (
        <View style={{paddingTop: topInset + 10}}>
            <View style={styles.headerTopRow}>
                <MailAccountChip
                    name={activeAccount?.senderName || activeAccount?.name}
                    email={activeAccount?.senderEmail}
                    onPress={() => navigation.navigate('MailSettingsScreen')}
                />
                <TouchableOpacity
                    style={[styles.searchBtn, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}
                    onPress={() => navigation.navigate('MailSearchScreen')}
                    activeOpacity={0.8}
                    accessibilityLabel={t('mail_search')}
                >
                    <Icon name="magnify" size={18} color={m.colors.heading} />
                </TouchableOpacity>
                <MailWriteButton label={t('mail_write_button')} onPress={openCompose} />
            </View>

            {/* Screen title — the active folder + a live one-line signal. */}
            <View style={styles.titleRow}>
                <Text style={[styles.screenTitle, {color: m.colors.heading}]} numberOfLines={1}>
                    {folderMeta(activeFolder, t).label}
                </Text>
                <Text style={[styles.screenSub, {color: m.colors.subText}]} numberOfLines={1}>
                    {loading && !total
                        ? t('mail_updating', {defaultValue: 'Updating…'})
                        : activeFolder === 'INBOX' && mail.unreadCount > 0
                            ? `${t('mail_unread_count', {count: mail.unreadCount, defaultValue: '{{count}} unread'})} · ${t('mail_list_total', {count: total, defaultValue: '{{count}} messages'})}`
                            : t('mail_list_total', {count: total, defaultValue: '{{count}} messages'})}
                </Text>
            </View>

            {(activeFolder === 'Trash' || activeFolder === 'Spam') && total > 0 && !selectionMode && (
                <TouchableOpacity
                    onPress={confirmEmptyFolder}
                    disabled={emptying}
                    activeOpacity={0.85}
                    style={[styles.emptyFolderBtn, {borderColor: m.colors.danger, opacity: emptying ? 0.5 : 1}]}
                >
                    <Icon name="trash-can-outline" size={16} color={m.colors.danger} />
                    <Text style={[styles.emptyFolderText, {color: m.colors.danger}]}>
                        {activeFolder === 'Trash' ? t('mail_empty_trash') : t('mail_empty_spam')}
                    </Text>
                </TouchableOpacity>
            )}

            {/* Folder chips */}
            <View style={styles.folderBar}>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.folderBarContent}>
                    {folderChips.map((f) => (
                        <FolderPill
                            key={f.key}
                            label={f.label}
                            icon={f.icon}
                            color={f.color}
                            count={f.count}
                            active={activeFolder === f.key}
                            onPress={() => setActiveFolder(f.key)}
                        />
                    ))}
                    {/* Create / rename / delete custom folders. */}
                    <FolderPill
                        label={t('mail_folders_manage_short')}
                        icon="folder-cog-outline"
                        active={false}
                        onPress={() => setFolderManagerOpen(true)}
                    />
                    {/* Send-later queue lives on its own screen (not a server folder). */}
                    <FolderPill
                        label={t('mail_scheduled_title')}
                        icon="clock-outline"
                        active={false}
                        onPress={() => navigation.navigate('MailScheduledScreen')}
                    />
                </ScrollView>
            </View>

            {/* Shared mailboxes (smart views + team tags), personal tags and the Snoozed view. */}
            {!selectionMode && (
                <MailCollabHomeChips collab={collab} view={collabView} onChange={onCollabChange} />
            )}

            {/* Organize filters: colour / AI category / label with counts (every account). */}
            {!selectionMode && (
                <MailOrganizeFilters summary={organizeSummary} filter={organizeFilter} onChange={setOrganizeFilter} />
            )}

            {/* Quick filter */}
            {!selectionMode && (
                <View style={styles.quickFilter}>
                    <QuickFilterBar value={quickFilter} options={quickFilterOptions} onChange={setQuickFilter} />
                </View>
            )}

            {/* Inline error banner — shown only while we still have rows to display. */}
            {!!error && filteredMessages.length > 0 && (
                <TouchableOpacity
                    onPress={retryFetch}
                    activeOpacity={0.85}
                    style={[styles.errorBanner, {backgroundColor: m.colors.surface, borderColor: m.colors.danger}]}
                >
                    <Icon name="cloud-off-outline" size={17} color={m.colors.danger} />
                    <Text style={[styles.errorBannerText, {color: m.colors.text}]} numberOfLines={1}>{t('mail_load_failed')}</Text>
                    <Text style={[styles.errorBannerAction, {color: m.colors.primary}]}>{t('mail_retry')}</Text>
                </TouchableOpacity>
            )}

            {listHeader}
        </View>
    );

    return (
        <ManagementScreen
            scroll={false}
            moduleGroup="mail"
            activeModuleTab={activeModuleTab}
            onModuleView={onModuleView}
            moduleBadges={moduleBadges}
            onModuleExit={exitToMainNav}
            floating={!selectionMode ? (
                <Animated.View
                    pointerEvents={fabVisible ? 'box-none' : 'none'}
                    style={[styles.fabLayer, {
                        bottom: fabBottom,
                        opacity: fabAnim,
                        transform: [{scale: fabAnim.interpolate({inputRange: [0, 1], outputRange: [0.85, 1]})}],
                    }]}
                >
                    <TouchableOpacity
                        onPress={openCompose}
                        activeOpacity={0.85}
                        accessibilityRole="button"
                        accessibilityLabel={t('mail_write_button')}
                    >
                        <LinearGradient
                            colors={m.gradients.primary}
                            start={{x: 0, y: 0}}
                            end={{x: 1, y: 1}}
                            style={[styles.fab, styles.fabGlow]}
                        >
                            <Icon name="pencil" size={23} color="#FFFFFF" />
                        </LinearGradient>
                    </TouchableOpacity>
                </Animated.View>
            ) : null}
        >
            {/* Selection toolbar — pinned premium pill with labeled bulk actions. */}
            {selectionMode && (
                <View style={[styles.selBar, {marginTop: topInset + 10, backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(2)]}>
                    <TouchableOpacity
                        onPress={() => setSelection([])}
                        hitSlop={8}
                        style={[styles.selClose, {backgroundColor: m.colors.surfaceAlt}]}
                        accessibilityRole="button"
                        accessibilityLabel={t('cancel')}
                    >
                        <Icon name="close" size={18} color={m.colors.heading} />
                    </TouchableOpacity>
                    <Text style={[styles.selCount, {color: m.colors.heading}]} numberOfLines={1}>
                        {t('mail_selected_count', {count: selection.length})}
                    </Text>
                    <View style={[styles.selDivider, {backgroundColor: m.colors.borderSoft}]} />
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.selActionsScroll} contentContainerStyle={styles.selActions}>
                        <TouchableOpacity
                            onPress={toggleSelectAllLoaded}
                            style={styles.selAct}
                            hitSlop={6}
                            accessibilityRole="button"
                            accessibilityState={{selected: allLoadedSelected}}
                            accessibilityLabel={t('mail_select_all')}
                        >
                            <Icon name={allLoadedSelected ? 'checkbox-multiple-marked' : 'checkbox-multiple-blank-outline'} size={20} color={allLoadedSelected ? m.colors.primary : m.colors.heading} />
                            <Text style={[styles.selActText, {color: allLoadedSelected ? m.colors.primary : m.colors.subText}]} numberOfLines={1}>{t('mail_act_all', {defaultValue: 'All'})}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => runBulk((ids) => mail.markRead(ids, true))} style={styles.selAct} hitSlop={6} accessibilityLabel={t('mail_action_mark_read')}>
                            <Icon name="email-open-outline" size={20} color={m.colors.heading} />
                            <Text style={[styles.selActText, {color: m.colors.subText}]} numberOfLines={1}>{t('mail_act_read', {defaultValue: 'Read'})}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => runBulk((ids) => mail.markRead(ids, false))} style={styles.selAct} hitSlop={6} accessibilityLabel={t('mail_mark_unread')}>
                            <Icon name="email-mark-as-unread" size={20} color={m.colors.heading} />
                            <Text style={[styles.selActText, {color: m.colors.subText}]} numberOfLines={1}>{t('mail_act_unread', {defaultValue: 'Unread'})}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => runBulk((ids) => mail.starMessages(ids, true))} style={styles.selAct} hitSlop={6} accessibilityLabel={t('mail_filter_starred')}>
                            <Icon name="star-outline" size={20} color={m.colors.heading} />
                            <Text style={[styles.selActText, {color: m.colors.subText}]} numberOfLines={1}>{t('mail_act_star', {defaultValue: 'Star'})}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => runBulk((ids) => mail.archiveMessages(ids))} style={styles.selAct} hitSlop={6} accessibilityLabel={t('mail_folder_archive')}>
                            <Icon name="archive-outline" size={20} color={m.colors.heading} />
                            <Text style={[styles.selActText, {color: m.colors.subText}]} numberOfLines={1}>{t('mail_act_archive', {defaultValue: 'Archive'})}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setOrganizeOpen(true)} style={styles.selAct} hitSlop={6} accessibilityLabel={t('mail_organize_title')}>
                            <Icon name="palette-outline" size={20} color={m.colors.heading} />
                            <Text style={[styles.selActText, {color: m.colors.subText}]} numberOfLines={1}>{t('mail_act_organize')}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setMovePickerOpen(true)} style={styles.selAct} hitSlop={6} accessibilityLabel={t('mail_rule_move_to')}>
                            <Icon name="folder-move-outline" size={20} color={m.colors.heading} />
                            <Text style={[styles.selActText, {color: m.colors.subText}]} numberOfLines={1}>{t('mail_act_move', {defaultValue: 'Move'})}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            onPress={() => runBulk(async (ids) => {
                                await mail.moveMessages(ids, 'Spam');
                                await mail.refreshFolders();
                            })}
                            style={styles.selAct}
                            hitSlop={6}
                            accessibilityLabel={t('mail_mark_spam')}
                        >
                            <Icon name="alert-octagon-outline" size={20} color={m.colors.heading} />
                            <Text style={[styles.selActText, {color: m.colors.subText}]} numberOfLines={1}>{t('mail_act_spam', {defaultValue: 'Spam'})}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => runBulk((ids) => mail.trashMessages(ids))} style={styles.selAct} hitSlop={6} accessibilityLabel={t('mail_folder_trash')}>
                            <Icon name="trash-can-outline" size={20} color={m.colors.danger} />
                            <Text style={[styles.selActText, {color: m.colors.danger}]} numberOfLines={1}>{t('mail_act_trash', {defaultValue: 'Trash'})}</Text>
                        </TouchableOpacity>
                        {activeFolder === 'Trash' && (
                            <TouchableOpacity onPress={confirmDeleteForever} style={styles.selAct} hitSlop={6} accessibilityLabel={t('mail_delete_forever')}>
                                <Icon name="delete-forever-outline" size={20} color={m.colors.danger} />
                                <Text style={[styles.selActText, {color: m.colors.danger}]} numberOfLines={1}>{t('mail_act_delete', {defaultValue: 'Delete'})}</Text>
                            </TouchableOpacity>
                        )}
                    </ScrollView>
                </View>
            )}

            {/* Message list — the premium header scrolls with it. */}
            {showSkeleton ? (
                <View style={styles.listFill}>
                    {headerContent}
                    <MailListSkeleton />
                </View>
            ) : showError ? (
                <ScrollView
                    style={styles.listFill}
                    contentContainerStyle={{paddingBottom: bottomInset + MODULE_BAR_HEIGHT + MODULE_BAR_OFFSET + 24}}
                    showsVerticalScrollIndicator={false}
                >
                    {headerContent}
                    <View style={styles.errorWrap}>
                        <View style={[styles.errorCard, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
                            <View style={[styles.errorIcon, {backgroundColor: m.colors.surfaceAlt}]}>
                                <Icon name="cloud-off-outline" size={26} color={m.colors.danger} />
                            </View>
                            <Text style={[styles.errorTitle, {color: m.colors.heading}]}>
                                {t('mail_list_error_title', {defaultValue: 'Could not load your mail'})}
                            </Text>
                            <Text style={[styles.errorText, {color: m.colors.subText}]}>
                                {t('mail_list_error_text', {defaultValue: 'Check your connection and try again.'})}
                            </Text>
                            <View style={styles.errorAction}>
                                <PrimaryButton label={t('mail_retry')} icon="refresh" onPress={retryFetch} />
                            </View>
                        </View>
                    </View>
                </ScrollView>
            ) : (
                <FlatList
                    data={listData}
                    keyExtractor={keyExtractor}
                    renderItem={renderItem}
                    extraData={selectedSet}
                    /* Clear the floating module bar so the last row stays tappable. */
                    contentContainerStyle={[styles.listContent, {paddingBottom: bottomInset + MODULE_BAR_HEIGHT + MODULE_BAR_OFFSET + 24}]}
                    showsVerticalScrollIndicator={false}
                    onScroll={onListScroll}
                    scrollEventThrottle={32}
                    refreshControl={(
                        <RefreshControl
                            refreshing={refreshing}
                            onRefresh={onRefresh}
                            tintColor={m.colors.primary}
                            colors={[m.colors.primary]}
                            progressBackgroundColor={m.colors.surface}
                        />
                    )}
                    onEndReached={onEndReached}
                    onEndReachedThreshold={0.5}
                    /* Virtualization tuning — the win on long mailboxes. */
                    removeClippedSubviews={Platform.OS === 'android'}
                    initialNumToRender={10}
                    maxToRenderPerBatch={8}
                    updateCellsBatchingPeriod={60}
                    windowSize={9}
                    ListHeaderComponent={headerContent}
                    ListEmptyComponent={snoozedView ? (
                        <MailEmpty
                            icon="alarm-snooze"
                            title={t('mail_collab_snoozed_empty_title')}
                            text={t('mail_collab_snoozed_empty_text')}
                            tone="primary"
                            actionLabel={t('mail_collab_clear')}
                            actionIcon="close"
                            onAction={() => onCollabChange(null)}
                        />
                    ) : quickFilter !== 'all' ? (
                        <MailEmpty
                            icon={quickFilter === 'starred' ? 'star-outline' : (quickFilter === 'attachments' ? 'paperclip' : 'email-check-outline')}
                            title={t('mail_filter_empty_title')}
                            text={t('mail_filter_empty_text')}
                            tone="primary"
                            actionLabel={t('mail_filter_all')}
                            actionIcon="filter-outline"
                            onAction={() => setQuickFilter('all')}
                        />
                    ) : (
                        <MailEmpty
                            icon={folderEmpty.icon}
                            title={folderEmpty.title || t('mail_empty_title')}
                            text={folderEmpty.text}
                            tone="primary"
                            actionLabel={folderEmpty.actionLabel}
                            actionIcon={folderEmpty.actionIcon}
                            onAction={folderEmpty.onAction}
                        />
                    )}
                    ListFooterComponent={listFooter}
                />
            )}

            {/* Organize bottom sheet (folder + labels + colour for the selection). */}
            <MailOrganizeSheet
                visible={organizeOpen}
                onClose={() => setOrganizeOpen(false)}
                messages={selectedMessages}
                folders={moveTargets}
                onApplied={() => setSelection([])}
            />

            {/* Move-to-folder bottom sheet (bulk selection → any other folder). */}
            <MailFolderPickerSheet
                visible={movePickerOpen}
                onClose={() => setMovePickerOpen(false)}
                title={t('mail_rule_move_to')}
                subtitle={t('mail_selected_count', {count: selection.length})}
                folders={moveTargets}
                onSelect={onMoveToFolder}
            />

            {/* Custom folder manager (create / rename / delete). */}
            <MailFolderManagerSheet
                visible={folderManagerOpen}
                onClose={() => setFolderManagerOpen(false)}
                onChanged={(deletedId) => {
                    if (deletedId && deletedId === activeFolder) {
                        setActiveFolder('INBOX');
                    }
                    mail.invalidate();
                }}
            />
        </ManagementScreen>
    );
};

const styles = StyleSheet.create({
    headerTopRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, gap: 10},
    titleRow: {paddingHorizontal: 16, marginTop: 14},
    emptyFolderBtn: {alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', marginLeft: 16, marginTop: 10, paddingHorizontal: 14, height: 36, borderRadius: 18, borderWidth: 1},
    emptyFolderText: {fontSize: 13, fontWeight: '800', marginLeft: 6},
    screenTitle: {fontSize: 30, fontWeight: '800', letterSpacing: -0.8},
    screenSub: {fontSize: 12.5, fontWeight: '600', marginTop: 3},
    searchBtn: {width: 42, height: 42, borderRadius: 21, borderWidth: 1, alignItems: 'center', justifyContent: 'center'},
    folderBar: {paddingTop: 10, paddingBottom: 8},
    folderBarContent: {paddingLeft: 14, paddingRight: 28, alignItems: 'center'},
    quickFilter: {paddingBottom: 8},
    selBar: {flexDirection: 'row', alignItems: 'center', marginHorizontal: 14, marginBottom: 6, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, borderWidth: 1, gap: 10},
    selClose: {width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center'},
    selCount: {fontSize: 13.5, fontWeight: '800'},
    selDivider: {width: StyleSheet.hairlineWidth, alignSelf: 'stretch', marginVertical: 2},
    selActionsScroll: {flexGrow: 1, flexShrink: 1},
    selActions: {flexDirection: 'row', alignItems: 'center', gap: 14, paddingRight: 4},
    selAct: {alignItems: 'center', justifyContent: 'center', minWidth: 46, gap: 2},
    selActText: {fontSize: 9.5, fontWeight: '700'},
    listContent: {flexGrow: 1},
    listFill: {flex: 1},
    rowWrap: {paddingHorizontal: 14},

    summaryRow: {flexDirection: 'row', alignItems: 'center', gap: 7, paddingHorizontal: 18, paddingBottom: 6},
    summaryText: {fontSize: 11, fontWeight: '600'},

    footerRow: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 18},
    footerText: {fontSize: 12.5, fontWeight: '700'},
    footerSpacer: {height: 10},

    errorBanner: {flexDirection: 'row', alignItems: 'center', gap: 9, marginHorizontal: 14, marginBottom: 8, paddingHorizontal: 14, paddingVertical: 11, borderRadius: 999, borderWidth: 1},
    errorBannerText: {flex: 1, fontSize: 13, fontWeight: '700'},
    errorBannerAction: {fontSize: 12.5, fontWeight: '900', textTransform: 'uppercase', letterSpacing: 0.4},

    errorWrap: {paddingHorizontal: 14, paddingTop: 8},
    errorCard: {borderRadius: 26, borderWidth: 1, padding: 22, alignItems: 'center'},
    errorIcon: {width: 56, height: 56, borderRadius: 20, alignItems: 'center', justifyContent: 'center', marginBottom: 14},
    errorTitle: {fontSize: 16, fontWeight: '900', textAlign: 'center'},
    errorText: {fontSize: 13.5, fontWeight: '600', textAlign: 'center', marginTop: 6, lineHeight: 19},
    errorAction: {marginTop: 16, alignSelf: 'stretch'},

    fabLayer: {position: 'absolute', right: 18},
    fab: {width: 58, height: 58, borderRadius: 29, alignItems: 'center', justifyContent: 'center'},
    fabGlow: {shadowColor: '#EA580C', shadowOffset: {width: 0, height: 10}, shadowOpacity: 0.36, shadowRadius: 16, elevation: 8},
});

export default MailHomeScreen;
